import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useInView, useScroll, useTransform } from "framer-motion";
import { ArrowDown, ArrowUpRight, Play } from "lucide-react";
import HeroScene from "./HeroScene";
import { useI18n } from "../context/I18nContext";

const DECK = [
  { id: "riviera-polo", name: "Riviera Knit Polo", line: "Look 02 · Riviera Evening", image: "/brand/looks/riviera-polo-v4.jpg" },
  { id: "signature-pique", name: "Monogram Piqué Polo", line: "Look 06 · Midnight Monogram", image: "/brand/looks/signature-pique-v4.jpg" },
  { id: "md-addiction", name: "MD Addiction", line: "Eau de Parfum · House Icon", image: "/brand/md-addiction.jpg" },
  { id: "ivory-poplin", name: "ATW Ivory Poplin Shirt", line: "Look 01 · The Boutique Hour", image: "/brand/looks/ivory-poplin-v4.jpg" },
  { id: "dewan-royal-oud", name: "Dewan Royal Oud", line: "Extrait de Parfum", image: "/brand/dewan-royal-oud.jpg" },
];

const POSES = [
  { x: 0, y: 0, rotate: 0, scale: 1, opacity: 1, zIndex: 5 },
  { x: 54, y: -22, rotate: 6, scale: 0.94, opacity: 0.9, zIndex: 4 },
  { x: 104, y: -44, rotate: 11, scale: 0.88, opacity: 0.7, zIndex: 3 },
  { x: 150, y: -64, rotate: 15, scale: 0.82, opacity: 0.45, zIndex: 2 },
  { x: -260, y: 40, rotate: -18, scale: 0.9, opacity: 0, zIndex: 1 },
];

const fade = (delay) => ({ initial: { opacity: 0, y: 18 }, animate: { opacity: 1, y: 0 }, transition: { delay, duration: 0.9, ease: "easeOut" } });
const word = { hidden: { y: "110%", rotateX: 40, opacity: 0 }, show: (i) => ({ y: 0, rotateX: 0, opacity: 1, transition: { delay: 0.35 + i * 0.16, duration: 1.2, ease: [0.22, 1, 0.36, 1] } }) };

const LOW_END = typeof navigator !== "undefined" && ((navigator.hardwareConcurrency || 8) <= 4 || window.matchMedia("(prefers-reduced-motion: reduce)").matches);

export default function HeroEditorial() {
  const { t } = useI18n();
  const ref = useRef(null);
  const [front, setFront] = useState(0);
  const inView = useInView(ref, { margin: "0px 0px -20% 0px" });
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const textY = useTransform(scrollYProgress, [0, 1], [0, -160]);
  const deckY = useTransform(scrollYProgress, [0, 1], [0, 120]);
  const opacity = useTransform(scrollYProgress, [0, 0.7], [1, 0]);

  useEffect(() => {
    const t = setInterval(() => setFront((v) => (v + 1) % DECK.length), 4200);
    return () => clearInterval(t);
  }, []);

  const s = DECK[front];

  return (
    <section ref={ref} data-testid="hero-section" className="relative min-h-[100svh] overflow-hidden bg-obsidian text-ivory">
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_30%_60%,rgba(212,175,55,0.14),transparent_55%)] pointer-events-none" />
      <div className="absolute inset-x-0 top-[62%] h-px bg-gradient-to-r from-transparent via-gold/40 to-transparent hidden lg:block" />
      <span className="hidden lg:block absolute left-14 top-1/2 -translate-y-1/2 [writing-mode:vertical-rl] rotate-180 text-[9px] tracking-[0.5em] uppercase text-ivory/30">Est. by Dewan Ali · Around The World Ltd</span>

      <div className="relative max-w-[1560px] mx-auto px-6 lg:px-24 pt-28 lg:pt-32 pb-24 min-h-[100svh] grid lg:grid-cols-12 gap-10 items-center">
        <motion.div style={{ y: textY, opacity }} className="relative lg:col-span-7 lg:pr-6">
          <div data-testid="hero-3d-scene" className="absolute -inset-x-10 -top-24 -bottom-16 hidden md:block opacity-90 pointer-events-none">
            {!LOW_END && <HeroScene active={inView} position={[1.6, -0.9, 0]} scale={0.72} />}
          </div>
          <div className="relative">
            <motion.p {...fade(0.1)} data-testid="hero-eyebrow" className="flex items-center gap-4 text-[10px] md:text-xs tracking-[0.5em] uppercase text-ivory/55">
              <span className="h-px w-10 bg-gold" />
              <Link to="/about" data-testid="hero-founder-link" className="hover:text-gold-light transition-colors">{t("hero_eyebrow")}</Link>
            </motion.p>
            <h1 style={{ perspective: 1000 }} className="mt-8 font-serif uppercase leading-[0.82] tracking-tight">
              {[
                <span key="a" className="block text-[17vw] md:text-[12vw] lg:text-[8.4vw] text-ivory">{t("hero_l1")}</span>,
                <span key="b" className="block text-[17vw] md:text-[12vw] lg:text-[8.4vw] gold-text italic font-light pl-[8vw] lg:pl-[5vw]">{t("hero_l2")}</span>,
              ].map((el, i) => (
                <span key={i} className="block overflow-hidden pb-2">
                  <motion.span custom={i} variants={word} initial="hidden" animate="show" className="block origin-bottom">{el}</motion.span>
                </span>
              ))}
            </h1>
            <motion.p {...fade(0.9)} className="mt-2 font-script text-4xl md:text-5xl text-gold-light/90 pl-1">{t("hero_sig")}</motion.p>

            <div className="mt-10 grid sm:grid-cols-[1fr_auto] gap-8 items-end">
              <motion.p {...fade(1)} className="max-w-sm text-sm md:text-base text-ivory/60 font-light leading-relaxed">
                {t("hero_copy")}
              </motion.p>
              <motion.div {...fade(1.1)} className="flex flex-wrap items-center gap-4">
                <Link to="/shop" data-testid="hero-shop-button" className="gold-button px-8 py-4 text-[11px] tracking-[0.25em] uppercase font-medium"><span className="relative z-10">{t("enter_store")}</span></Link>
                <Link to="/lookbook" data-testid="hero-bestsellers-button" className="group flex items-center gap-2 px-2 py-4 text-[11px] tracking-[0.25em] uppercase text-ivory/70 hover:text-gold-light transition-colors">
                  <span className="w-8 h-8 rounded-full border border-gold/50 flex items-center justify-center group-hover:bg-gold-light group-hover:text-obsidian transition-colors"><Play size={10} className="ml-0.5" /></span>
                  {t("the_lookbook")}
                </Link>
              </motion.div>
            </div>
          </div>
        </motion.div>

        <motion.div style={{ y: deckY }} className="lg:col-span-5 relative h-[62vh] lg:h-[74vh] flex items-center justify-center lg:justify-end" data-testid="hero-deck">
          <div className="relative w-[70%] max-w-[380px] aspect-[3/4]" style={{ perspective: 1400 }}>
            {DECK.map((card, k) => {
              const pos = POSES[(k - front + DECK.length) % DECK.length];
              return (
                <motion.div
                  key={card.id}
                  data-testid={`hero-deck-card-${card.id}`}
                  animate={pos}
                  transition={{ type: "spring", stiffness: 90, damping: 18 }}
                  style={{ zIndex: pos.zIndex, transformOrigin: "bottom right" }}
                  className="absolute inset-0 overflow-hidden border border-gold/30 bg-coal shadow-[0_50px_100px_-40px_rgba(212,175,55,0.45)]"
                >
                  <img src={card.image} alt={card.name} className="h-full w-full object-cover object-[50%_15%]" draggable={false} />
                  <div className="absolute inset-0 bg-gradient-to-t from-obsidian/80 via-transparent to-transparent" />
                  <span className="absolute top-4 left-4 text-[9px] tracking-[0.3em] uppercase glass-dark px-3 py-1.5 text-ivory">{card.line}</span>
                </motion.div>
              );
            })}
          </div>
          <div className="absolute -bottom-2 lg:-bottom-12 left-1/2 lg:left-auto lg:right-0 -translate-x-1/2 lg:translate-x-0 flex flex-col items-center lg:items-end gap-3">
            <Link to={`/product/${s.id}`} data-testid="hero-featured-link" className="glass-dark px-5 py-3.5 flex items-center gap-4 hover:border-gold/60 transition-colors min-w-[250px]">
              <div>
                <p className="text-[9px] tracking-[0.3em] uppercase text-gold-light/80">{s.line}</p>
                <p data-testid="hero-slide-name" className="font-serif text-lg mt-0.5">{s.name}</p>
              </div>
              <ArrowUpRight size={16} className="ml-auto text-gold-light" />
            </Link>
            <div className="flex gap-2">
              {DECK.map((x, k) => (
                <button key={x.id} data-testid={`hero-slide-dot-${k}`} onClick={() => setFront(k)} aria-label={x.name} className={`h-px transition-[width,background-color] duration-500 ${k === front ? "w-10 bg-gold-light" : "w-4 bg-ivory/30"}`} />
              ))}
            </div>
          </div>
        </motion.div>
      </div>

      <motion.div {...fade(1.6)} className="absolute bottom-8 left-6 lg:left-24 z-10 flex items-center gap-3 text-ivory/45">
        <motion.span animate={{ y: [0, 7, 0] }} transition={{ repeat: Infinity, duration: 1.8, ease: "easeInOut" }}><ArrowDown size={15} strokeWidth={1.5} /></motion.span>
        <span className="text-[9px] tracking-[0.4em] uppercase">{t("scroll")}</span>
      </motion.div>
    </section>
  );
}
